import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, User } from '../types';
import { apiService } from '../services/api';

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  getCurrentUser: () => Promise<void>;
  updateUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        console.log('Login attempt:', email);
        set({ isLoading: true });
        try {
          const response = await apiService.login({ email, password });
          if (response.success && response.data) {
            const { user, token } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
            console.log('Login successful');
          } else {
            // Handle both 'error' and 'message' fields in the response
            const errorMessage = (response as any).error || response.message || 'Login failed';
            throw new Error(errorMessage);
          }
        } catch (error: any) {
          console.error('Login error in store:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (name: string, email: string, password: string) => {
        console.log('Register attempt:', email);
        set({ isLoading: true });
        try {
          const response = await apiService.register({ name, email, password });
          if (response.success && response.data) {
            const { user, token } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
            console.log('Register successful');
          } else {
            // Handle both 'error' and 'message' fields in the response
            const errorMessage = (response as any).error || response.message || 'Registration failed';
            throw new Error(errorMessage);
          }
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        console.log('Logout function called');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('auth-storage');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        console.log('Logout completed, state updated');
        console.log('Current localStorage after logout:', {
          token: localStorage.getItem('token'),
          user: localStorage.getItem('user'),
          authStorage: localStorage.getItem('auth-storage')
        });
      },

      getCurrentUser: async () => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        console.log('getCurrentUser called, token exists:', !!token);
        
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await apiService.getCurrentUser();
          if (response.success && response.data) {
            set({
              user: response.data,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
            console.log('User loaded from API');
          } else {
            // Fallback to saved user if API fails
            if (savedUser) {
              const user = JSON.parse(savedUser);
              set({
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
              });
              console.log('User loaded from localStorage fallback');
            } else {
              localStorage.removeItem('token');
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
              });
              console.log('No user data available');
            }
          }
        } catch (error: any) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          console.log('Error loading user:', error);
        }
      },

      updateUser: (user: User) => {
        set({ user });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        console.log('Auth store rehydrated:', state);
        // Also update localStorage when rehydrating
        if (state?.user && state?.token) {
          localStorage.setItem('token', state.token);
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      },
    }
  )
);